import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { Row, Col } from "react-bootstrap";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useQuery } from "react-query";

import { HookSelectFormGroup } from "components";
import { ViewContainer } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { ScheduleAStatus } from "mappers";
import { dropdownValuesService } from "services";
import { requiredDropDownScheduleA } from "validators";

import ScheduleAOption from "./scheduleAOption";

import { ScheduleAView } from "./index";

import styles from "./scheduleAView.module.scss";

export type ScheduleADataToCheckRequirement = {
	sponsorId: number;
	agentPartnerType: number;
	businessUnit: string;
};

type ScheduleAItem = {
	value: string;
	name: string;
};

type ScheduleAViewSelectProps = {
	agentId?: string;
	processorId?: number;
	isEditable: boolean;
	canViewResidualsDetails: boolean;
	initialScheduleA: string;
	isLoadingData?: boolean;
	dataToCheckRequirement?: ScheduleADataToCheckRequirement;
	section?: "Merchant" | "Underwriting";
	setHandleSubmit?: Function;
	onError?: any;
	onChange?: Function;
	defaultValue?: any;
};
export const ScheduleAViewSelect = ({
	agentId,
	processorId,
	isEditable,
	canViewResidualsDetails,
	initialScheduleA,
	isLoadingData,
	dataToCheckRequirement,
	setHandleSubmit,
	onError,
	onChange,
	defaultValue = "",
	section,
}: ScheduleAViewSelectProps) => {
	const [prevValue, setPrevValue] = useState<ScheduleAItem>();

	const methods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
	});

	const { control, setValue, handleSubmit } = methods;

	const scheduleAList: ScheduleAItem | undefined = useWatch({
		control,
		name: "scheduleAList",
		defaultValue: defaultValue,
	});

	const options = { showInactive: true, sortBy: "name", ascending: true };

	const {
		data: scheduleAItems,
		isFetching,
		isSuccess,
		error,
	} = useQuery(
		["scheduleAList", agentId, options],
		() => dropdownValuesService.getScheduleAList(agentId, options),
		{
			select: data => {
				const scheduleAWithSpecificProcessor = data?.filter(
					scheduleA => scheduleA?.processor_id === processorId,
				);

				const scheduleAWithoutProcessor = data?.filter(
					scheduleA => scheduleA?.processor_id === null,
				);

				const scheduleAWithActiveSpecificProcessor = data?.filter(
					scheduleA =>
						scheduleA?.processor_id === processorId &&
						scheduleA?.status === ScheduleAStatus.AVAILABLE,
				);

				const inactiveSpecificProcessor = data?.filter(
					scheduleA =>
						scheduleA?.processor_id === processorId &&
						scheduleA?.status === ScheduleAStatus.UNAVAILABLE,
				);

				const availableWithNotProcessorsScheduleAs = data?.filter(
					scheduleA =>
						scheduleA?.processor_id === null &&
						scheduleA?.status === ScheduleAStatus.AVAILABLE,
				);

				const unavailableWithNotProcessorsScheduleAs = data?.filter(
					scheduleA =>
						scheduleA?.processor_id === null &&
						scheduleA?.status === ScheduleAStatus.UNAVAILABLE,
				);

				const value = () => {
					if (section === "Underwriting") {
						if (!isEmpty(scheduleAWithSpecificProcessor)) {
							return scheduleAWithSpecificProcessor;
						} else if (!isEmpty(scheduleAWithoutProcessor)) {
							return scheduleAWithoutProcessor;
						} else {
							return [];
						}
					} else {
						if (!processorId) {
							return data;
						} else if (
							!isEmpty(scheduleAWithActiveSpecificProcessor) &&
							!isEmpty(inactiveSpecificProcessor)
						) {
							return scheduleAWithSpecificProcessor;
						} else if (
							!isEmpty(scheduleAWithActiveSpecificProcessor) &&
							isEmpty(inactiveSpecificProcessor)
						) {
							return scheduleAWithActiveSpecificProcessor;
						} else if (
							!isEmpty(inactiveSpecificProcessor) &&
							!isEmpty(availableWithNotProcessorsScheduleAs)
						) {
							return [
								...inactiveSpecificProcessor,
								...availableWithNotProcessorsScheduleAs,
							];
						} else if (
							isEmpty(scheduleAWithSpecificProcessor) &&
							(!isEmpty(availableWithNotProcessorsScheduleAs) ||
								!isEmpty(
									unavailableWithNotProcessorsScheduleAs,
								))
						) {
							return [
								...unavailableWithNotProcessorsScheduleAs,
								...availableWithNotProcessorsScheduleAs,
							];
						} else if (!isEmpty(inactiveSpecificProcessor)) {
							return inactiveSpecificProcessor;
						} else {
							return scheduleAWithoutProcessor;
						}
					}
				};

				const result = value();

				return [
					...result.map(item => ({
						value: item.id,
						name: item.name,
						status: item.status,
						processorId: item?.processor_id,
					})),
				];
			},
			enabled:
				!!agentId &&
				(section === "Underwriting" ? !!processorId : !isLoadingData),
		},
	);

	const currentScheduleAItem = useMemo(() => {
		const value = scheduleAItems?.find(
			item => item.value === initialScheduleA,
		);
		prevValue === undefined && setPrevValue(value);
		return value;
	}, [initialScheduleA, prevValue, scheduleAItems]);

	const isLoading = useMemo(
		() => !agentId || isLoadingData || isFetching,
		[agentId, isLoadingData, isFetching],
	);

	useEffect(() => {
		setValue("scheduleAList", currentScheduleAItem);
	}, [currentScheduleAItem, setValue, scheduleAItems, isLoading]);

	const { isFieldRequired, isShowWarnings } = useMemo(() => {
		if (isEmpty(dataToCheckRequirement)) {
			return {};
		}
		const currentPartnerType = dataToCheckRequirement?.agentPartnerType;
		const currentBusinessUnit = dataToCheckRequirement?.businessUnit;

		if (
			currentPartnerType !== undefined &&
			currentBusinessUnit !== undefined
		) {
			return {
				isFieldRequired:
					Array.isArray(scheduleAItems) && scheduleAItems?.length <= 0
						? false
						: true,
				isShowWarnings: true,
			};
		}

		return {};
	}, [dataToCheckRequirement, scheduleAItems]);

	const handleChange = useCallback(
		value => {
			onChange?.(value, () => {
				setValue("scheduleAList", prevValue);
				setPrevValue(prevValue);
			});
			setPrevValue(value);
		},
		[onChange, prevValue, setValue],
	);

	useEffect(() => {
		setHandleSubmit?.(
			() => () =>
				new Promise((resolve, reject) => {
					isSuccess ? resolve(handleSubmit) : reject(error);
				}),
		);
	}, [error, handleSubmit, isSuccess, setHandleSubmit]);

	const scheduleAWarningMessage = () => {
		return Array.isArray(scheduleAItems) && scheduleAItems?.length <= 0 ? (
			<small className="text-warning">
				No Schedule A available on agent profile. Please contact Agent
				Relations to set one up to avoid any delay to residual payments.
			</small>
		) : null;
	};

	return (
		<>
			<ViewContainer>
				<FormProvider {...methods}>
					<Row>
						<Col sm={12}>
							<HookSelectFormGroup
								label="Schedule A"
								options={scheduleAItems}
								getOptionLabel={ScheduleAOption}
								getOptionValue={option => option.value}
								placeholder="Please Select"
								name="scheduleAList"
								items={scheduleAItems}
								isSearchable={false}
								cacheOptions
								defaultOptions
								defaultValue={currentScheduleAItem}
								debounceTimeout={DebounceTimeout}
								blurLoading={isLoading}
								colLabel={{ sm: 3 }}
								colSelect={{ sm: 8 }}
								containerClassName={styles.selectContainer}
								isDisabled={!isEditable}
								onChange={handleChange}
								rules={{
									validate: isFieldRequired && {
										requiredDropDownScheduleA,
									},
								}}
								showRequiredMark={isFieldRequired}
							/>
							{isShowWarnings && scheduleAWarningMessage()}
						</Col>
					</Row>
				</FormProvider>
			</ViewContainer>
			{scheduleAList?.value && agentId && canViewResidualsDetails && (
				<div className={styles.contentContainer}>
					<ScheduleAView
						userId={agentId}
						scheduleId={scheduleAList?.value}
						onError={onError}
					/>
				</div>
			)}
		</>
	);
};
